import { useApolloClient, type Reference } from '@apollo/client';
import { Card, CardContent, Stack } from '@mui/material';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';
import { ErrorBoundaryFallback } from '@components/ErrorBoundaryFallback';
import PaymentModalContent from '@components/Modal/Content/PaymentModalContent';

import { PlanType } from '@constants/plans';
import { RoleName } from '@constants/role_name';

import { useGetAccount } from '@hooks/api/account';
import { useGetStripeCustomer } from '@hooks/api/stripe';
import type { CostCalculatorCosts } from '@hooks/useCostCalculator';
import { useFeatureFlags } from '@hooks/useFeatureFlags';
import { useModal } from '@hooks/useModal';
import { useSession } from '@hooks/useSession';
import { useSnackbar } from '@hooks/useSnackbar';

import type { OnboardingMember } from '@models/onboarding-member';
import { pickAnsweredResponses } from '@models/onboarding-member';
import { toPhoneContactInputs } from '@models/patient';
import { DEMOGRAPHICS_QUESTIONNAIRE_IDENTIFIER } from '@models/questionnaire';
import type { Profile } from '@models/session';

import { EmailInvitation } from '@views/Admin/NewAccount/Individual/EmailInvitation';
import NewAccountFormCollection, {
  defaultMember,
} from '@views/Admin/NewAccount/Individual/NewAccountFormCollection';
import PaymentSummary from '@views/Admin/NewAccount/Individual/PaymentSummary';

import type { TaxRate } from 'generated/types';
import { usePostUserRecord } from 'generated/user.generated';

import PlanSelection from './PlanSelection';

const messages = defineMessages({
  userCreated: {
    id: 'addNewProfile.userCreated',
    defaultMessage: 'User created',
  },
  createError: {
    id: 'addNewProfile.createError',
    defaultMessage: 'Could not create user',
  },
  addProfile: {
    id: 'addNewProfile.addProfile',
    defaultMessage: 'Add a Plan Member',
  },
  loadingSubscriptions: {
    id: 'addNewProfile.loadingSubscriptions',
    defaultMessage: 'Creating subscriptions',
  },
  loadingSecondary: {
    id: 'addNewProfile.loadingSecondary',
    defaultMessage: "Setting up {name}'s account.",
  },
  paymentProcessingFailed: {
    id: 'addNewProfile.paymentProcessingFailed',
    defaultMessage: 'Invoice could not be processed, payment failed',
  },
  addingFor: {
    id: 'addNewProfile.addingFor',
    defaultMessage: "Adding plan member to {name}'s account",
  },
});

interface AddNewProfileProps {
  account_id?: string;
  maxUsers?: number;
}

enum Step {
  SelectPlan = 0,
  UserDetails = 1,
  Summary = 2,
}

const AddNewProfile = ({
  account_id = '',
  maxUsers = 1,
}: AddNewProfileProps) => {
  const {
    state: { isVitall, loggedInProfile, activeProfile },
  } = useSession();
  const client = useApolloClient();
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const { toggleModal, openModal } = useModal();
  const [sendInvite, setSendInvite] = useState(true);
  const { flags } = useFeatureFlags();

  const [createUser] = usePostUserRecord();
  const { data: accountData, loading: accountLoading } = useGetAccount({
    variables: {
      input: {
        account_id,
      },
    },
  });
  const account = accountData?.getAccountRecord;
  const { data: stripeCustomer, loading: stripeCustomerLoading } =
    useGetStripeCustomer({
      account_id: account?.account_id ?? '',
    });

  const stripeInfo = stripeCustomer?.getStripeCustomer;

  /**
   * Patients can be added if active user is
   * 1) not managed (org) or
   * 2) their org is adding patients for them
   * 3) vitall is adding patients for them
   * 4) the user has permission to add other users
   */

  const canAddPatients =
    (loggedInProfile?.account_id === activeProfile?.account?.parent_id ||
      !activeProfile?.account?.isManaged ||
      isVitall) &&
    flags.isAccessManagementInviteUserEnabled;

  const loading = accountLoading || stripeCustomerLoading;

  const [members, setMembers] = useState<OnboardingMember[]>([
    { ...defaultMember },
  ]);
  const [planType, setPlanType] = useState<PlanType>();
  const [step, setStep] = useState(Step.SelectPlan);
  const [loadingStep, setLoadingStep] = useState('');

  const resetState = () => {
    setPlanType(undefined);
    setMembers([{ ...defaultMember }]);
    setLoadingStep('');
  };

  // Reset state on first step, avoids confusion
  React.useEffect(() => {
    if (step === Step.SelectPlan) {
      resetState();
    }
  }, [step]);

  const handleSelectPlan = (planType: PlanType) => {
    setPlanType(planType);
    setStep(Step.UserDetails);
  };

  const handleSubmitPlanMembers = (values: { members: OnboardingMember[] }) => {
    setMembers(values.members);
    setStep(Step.Summary);
  };

  async function handleSubmitPayment(_calculatedCosts: {
    costs: CostCalculatorCosts;
    taxRate: TaxRate | null;
  }) {
    if (!account) {
      return;
    }

    // Provision secondary users
    const newUsers: Profile[] = [];
    for (let i = 0; i < members.length; i += 1) {
      setLoadingStep(
        formatMessage(messages.loadingSecondary, { name: members[i].firstName })
      );

      const { dateOfBirth, phones, questionnaireResponses } = members[i];
      const answeredQuestionnaireResponses = pickAnsweredResponses(
        questionnaireResponses
      );
      const phoneInputs = toPhoneContactInputs(phones);

      const createUserResponse = await createUser({
        variables: {
          account_id: account.account_id,
          input: {
            first_name: members[i].firstName,
            middle_name: members[i].middleName,
            last_name: members[i].lastName,
            email: members[i].email,
            // TODO Extend to other roles
            roles: [members[i].isPatient ? RoleName.Patient : RoleName.Client],
            toc_completed: members[i].consents?.terms,
            pn_completed: members[i].consents?.terms,
            casl_completed: members[i].consents?.contact,
            send_invite: sendInvite,
            patientInputs: dateOfBirth
              ? {
                  date_of_birth: dayjs(dateOfBirth).format('YYYY-MM-DD'),
                  ...(phoneInputs && { phones: phoneInputs }),
                  ...(Object.keys(answeredQuestionnaireResponses).length >
                    0 && {
                    questionnaire: {
                      identifier: DEMOGRAPHICS_QUESTIONNAIRE_IDENTIFIER,
                      input: answeredQuestionnaireResponses,
                    },
                  }),
                }
              : undefined,
          },
        },
      });
      const secondaryUser = createUserResponse.data?.postUserRecord;
      if (!secondaryUser) {
        enqueueSnackbar({
          variant: 'error',
          message: createUserResponse.errors?.[0].message,
        });
        return;
      }
      newUsers.push({
        ...secondaryUser,
        patient_id: secondaryUser.vitall_patient?.patient_id ?? null,
        vitall_patient_id:
          secondaryUser.vitall_patient?.vitall_patient_id ?? null,
      });
    }

    // Add new users to account owner's accessible users
    const normalizedAccountOwnerId = client.cache.identify({
      __typename: 'UserModel',
      user_id: account.owner_id,
    });
    client.cache.modify<{ users: Reference[] }>({
      id: client.cache.identify({ id: normalizedAccountOwnerId }),
      fields: {
        users: (existing, { toReference }) => {
          return [
            ...existing,
            ...newUsers.map((user) => toReference(user)).filter((ref) => !!ref),
          ];
        },
      },
    });

    openModal({
      width: 400,
      children: (
        <PaymentModalContent
          inputCallback={toggleModal}
          didPay={false}
          didSendEmail={sendInvite}
        />
      ),
    });
    // Go to first step
    setStep(Step.SelectPlan);
  }

  return (
    <ContentLoader loading={loading}>
      {step === Step.SelectPlan && (
        <PlanSelection
          onSelectPlan={handleSelectPlan}
          canAddPatients={canAddPatients}
        />
      )}
      {step === Step.UserDetails && (
        <Stack gap={1}>
          {isVitall && (
            <EmailInvitation sendInvite={sendInvite} onChange={setSendInvite} />
          )}

          {/* Date calendar is cut off if overflow not visible */}
          <Card sx={{ overflow: 'visible' }}>
            <CardContent>
              <NewAccountFormCollection
                isPatient={planType === PlanType.Patient}
                initialMembers={members}
                isAddForm
                maxUsers={maxUsers}
                onBack={() => setStep(Step.SelectPlan)}
                onSubmit={handleSubmitPlanMembers}
                parentProps={
                  account?.parent_id
                    ? {
                        parent_id: account.parent_id,
                        shouldFetchPlans: canAddPatients,
                      }
                    : undefined
                }
              />
            </CardContent>
          </Card>
        </Stack>
      )}

      {step === Step.Summary && (
        <Card>
          <CardContent>
            <PaymentSummary
              isAddForm
              loadingStep={loadingStep}
              members={members}
              onBack={() => setStep(Step.UserDetails)}
              onSubmit={handleSubmitPayment}
              payment={
                stripeInfo
                  ? {
                      name: stripeInfo.name ?? '',
                      phone: '',
                      line1: stripeInfo.address?.line1 ?? '',
                      line2: stripeInfo.address?.line2 ?? '',
                      city: stripeInfo.address?.city ?? '',
                      country: (stripeInfo.address?.country ?? 'CA') as
                        'CA' | 'US',
                      postalCode: stripeInfo.address?.postal_code ?? '',
                      province: stripeInfo.address?.state ?? '',
                      couponCode: '',
                      coupon: null,
                      token: null,
                    }
                  : null
              }
            />
          </CardContent>
        </Card>
      )}
    </ContentLoader>
  );
};

export default withErrorBoundary(AddNewProfile, {
  FallbackComponent: ErrorBoundaryFallback,
});
